import React, { useState } from 'react';

const CloseIcon: React.FC = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
);

const PlusIcon: React.FC = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
    </svg>
);

const ArrowRightIcon: React.FC = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
    </svg>
);

interface CompareLaptopsModalProps {
    onClose: () => void;
    onCompare: (laptops: string[]) => void;
    isLoading: boolean;
}

export const CompareLaptopsModal: React.FC<CompareLaptopsModalProps> = ({
    onClose,
    onCompare,
    isLoading
}) => {
    const [laptops, setLaptops] = useState<string[]>(['', '']);

    const handleLaptopChange = (index: number, value: string) => {
        const updated = [...laptops];
        updated[index] = value;
        setLaptops(updated);
    };

    const handleAddLaptop = () => {
        if (laptops.length < 5) {
            setLaptops([...laptops, '']);
        }
    };

    const handleRemoveLaptop = (index: number) => {
        if (laptops.length > 2) {
            setLaptops(laptops.filter((_, i) => i !== index));
        }
    };

    const handleCompare = () => {
        const filledLaptops = laptops.filter(l => l.trim());
        if (filledLaptops.length >= 2) {
            onCompare(filledLaptops);
        }
    };

    const isFilled = laptops.filter(l => l.trim()).length >= 2;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 animate-fade-in">
            <div className="bg-[var(--surface-2)] border border-[var(--surface-4)] rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-slide-in">
                <div className="sticky top-0 bg-[var(--surface-2)] border-b border-[var(--surface-4)] p-6 sm:p-8 flex items-center justify-between">
                    <h2 className="text-2xl sm:text-3xl font-bold text-[var(--text-1)] font-serif">
                        Compare Laptops
                    </h2>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-[var(--surface-3)] rounded-lg transition-colors text-[var(--text-2)] hover:text-[var(--text-1)]"
                    >
                        <CloseIcon />
                    </button>
                </div>

                <div className="p-6 sm:p-8">
                    <p className="text-[var(--text-2)] mb-6 text-sm sm:text-base">
                        Enter the laptop models you'd like to compare. You can add up to 5 laptops.
                    </p>

                    <div className="space-y-4 mb-6">
                        {laptops.map((laptop, index) => (
                            <div key={index} className="flex items-end gap-3">
                                <div className="flex-grow">
                                    <label className="block text-sm font-semibold text-[var(--text-1)] mb-2">
                                        Laptop {index + 1}
                                    </label>
                                    <input
                                        type="text"
                                        value={laptop}
                                        onChange={(e) => handleLaptopChange(index, e.target.value)}
                                        placeholder="e.g., MacBook Pro 16 M4 Max"
                                        className="w-full px-4 py-3 bg-[var(--surface-1)] text-[var(--text-1)] placeholder-[var(--text-3)] rounded-lg border border-[var(--surface-4)] focus:ring-2 focus:ring-[var(--brand)] focus:outline-none transition-shadow text-sm sm:text-base"
                                        disabled={isLoading}
                                    />
                                </div>
                                {laptops.length > 2 && (
                                    <button
                                        onClick={() => handleRemoveLaptop(index)}
                                        className="p-3 text-[var(--text-3)] hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-colors mb-0"
                                        disabled={isLoading}
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>

                    {laptops.length < 5 && (
                        <button
                            onClick={handleAddLaptop}
                            className="w-full py-3 border-2 border-dashed border-[var(--surface-4)] rounded-lg text-[var(--text-2)] hover:border-[var(--brand)] hover:text-[var(--brand)] hover:bg-[var(--brand)]/5 transition-all flex items-center justify-center gap-2 font-semibold mb-6"
                            disabled={isLoading}
                        >
                            <PlusIcon />
                            Add Another Laptop
                        </button>
                    )}

                    <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                        <button
                            onClick={onClose}
                            className="flex-1 py-3 px-6 border border-[var(--surface-4)] text-[var(--text-1)] font-semibold rounded-lg hover:bg-[var(--surface-3)] transition-colors disabled:opacity-50"
                            disabled={isLoading}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleCompare}
                            disabled={!isFilled || isLoading}
                            className="flex-1 py-3 px-6 bg-[var(--brand)] text-[var(--brand-text)] font-semibold rounded-lg hover:bg-[var(--brand-hover)] disabled:bg-[var(--surface-4)] disabled:text-[var(--text-3)] disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 shadow-sm hover:shadow-md"
                        >
                            {isLoading ? (
                                <>
                                    <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Comparing...
                                </>
                            ) : (
                                <>
                                    Compare <ArrowRightIcon />
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
